const SATELLITE_RETURN_TO_PATTERNS = [
  /^https:\/\/[^/]+\.seam\.vc(?:[/?#]|$)/,
  /^https:\/\/connect\.getseam\.com(?:[/?#]|$)/,
  // The primary console itself, so a logged-out user deep-linking to a
  // /dashboard/* route (e.g. the CLI wizard handoff, which must keep its
  // cli_port/cli_state query) returns to that exact URL after sign-in rather
  // than landing on the homepage. Same origin, so it is not an open redirect.
  /^https:\/\/console\.seam\.co(?:[/?#]|$)/,
]

/**
 * Clerk's Frontend API host, decoded from the publishable key
 * (`pk_<env>_<base64("host$")>`). Clerk's OAuth authorization server lives on
 * this host: when an MCP client (Claude, Cursor, …) starts an OAuth flow and
 * the user is signed out, Clerk sends them to our /signin with
 * `redirect_url=https://<this host>/oauth/authorize…`, and we must return
 * there after sign-in for the client's flow to complete.
 */
const getClerkFrontendApiHost = (): string | null => {
  const publishableKey = process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY
  if (publishableKey == null) return null
  const encodedHost = publishableKey.split("_")[2]
  if (encodedHost == null) return null
  try {
    const decodedHost = atob(encodedHost)
    return decodedHost.endsWith("$") ? decodedHost.slice(0, -1) : null
  } catch {
    return null
  }
}

const isClerkOAuthAuthorizeUrl = (returnTo: string): boolean => {
  const clerkFrontendApiHost = getClerkFrontendApiHost()
  if (clerkFrontendApiHost == null) return false
  let url: URL
  try {
    url = new URL(returnTo)
  } catch {
    return false
  }
  return (
    url.protocol === "https:" &&
    url.host === clerkFrontendApiHost &&
    url.pathname.startsWith("/oauth/")
  )
}

// Same origin as the page itself (e.g. http://localhost:3000 in local dev,
// where the hardcoded console.seam.co rule above doesn't match). Clerk routes
// its self-hosted /oauth-consent redirect through /signin, so the target can
// be one of our own pages. Same origin is never an open redirect.
const isSameOriginUrl = (returnTo: string): boolean => {
  if (typeof window === "undefined") return false
  try {
    return new URL(returnTo).origin === window.location.origin
  } catch {
    return false
  }
}

export const isAllowedClerkReturnTo = (
  returnTo: string | null | undefined
): returnTo is string =>
  typeof returnTo === "string" &&
  (SATELLITE_RETURN_TO_PATTERNS.some((pattern) => pattern.test(returnTo)) ||
    isClerkOAuthAuthorizeUrl(returnTo) ||
    isSameOriginUrl(returnTo))
